/**
 * Add / edit a customer or supplier.
 *
 * WHY THE FORM MIRRORS THE SERVICE'S RULES INSTEAD OF INVENTING ITS OWN
 * ------------------------------------------------------------------
 * Every validator here is the SAME function the service calls
 * (`masterDataRules`). A form with its own idea of a valid GSTIN would show a
 * green tick and then refuse the save, which is the kind of thing a shopkeeper
 * learns to work around by typing nonsense.
 *
 * The submit button stays live while a field is merely incomplete, and the reason
 * is shown on the field. A permanently disabled save button with no message is
 * indistinguishable from a broken app; a live button that says what is wrong is a
 * form.
 */
import { useMemo, useState } from 'react';
import { Drawer } from '@ui/components/Drawer';
import { Form } from '@ui/components/Form';
import { Button } from '@ui/components/Button';
import { Field, Input, Textarea } from '@ui/components/Field';
import { ConfirmDialog } from '@ui/components/Modal';
import { GST_STATES } from '@core/gst/stateCodes';
import { collectProblems } from '@ui/lib/formValidation';
import {
  emailField,
  gstinField,
  partyNameField,
  phoneField,
  stateCodeField,
  stateMatchesGstin,
} from '@features/masterData/masterDataRules';
import { useCreateParty, useUpdateParty } from './usePartyWrites';
import type { Party, PartyKind } from '@data/db/types';

interface Props {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly kind: PartyKind;
  /** Null means CREATE; a row means EDIT. */
  readonly party: Party | null;
}

interface FormState {
  name: string;
  code: string;
  gstin: string;
  stateCode: string;
  billing: string;
  shipping: string;
  shippingStateCode: string;
  phone: string;
  email: string;
}

const BLANK: FormState = {
  name: '',
  code: '',
  gstin: '',
  stateCode: '',
  billing: '',
  shipping: '',
  shippingStateCode: '',
  phone: '',
  email: '',
};

/** The address textarea is one field per line; the model stores an array. */
const lines = (text: string): string[] =>
  text
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l !== '');

const toText = (value: readonly string[] | undefined): string => (value ?? []).join('\n');

export function PartyEditorDrawer({ open, onClose, kind, party }: Props) {
  const [state, setState] = useState<FormState>(BLANK);
  const [loadedId, setLoadedId] = useState<string | null>(null);
  /** A refusal from the service, as words. Null when the last save was fine. */
  const [saveError, setSaveError] = useState<string | null>(null);
  /** Set when the service finds a soft-duplicate BEFORE saving. */
  const [duplicateWarning, setDuplicateWarning] = useState<Party | null>(null);
  /** Guards against double-clicking submit causing a second mutation. */
  const [submitting, setSubmitting] = useState(false);
  const create = useCreateParty();
  const update = useUpdateParty();

  /**
   * Seed once per opened record.
   *
   * Guarded by `loadedId` for the same reason the Delivery Note editor is: a
   * refetch would otherwise reset whatever the user had just typed back to the
   * saved values, which reads as the form ignoring the keyboard.
   */
  const targetId = party?.id ?? 'new';
  /**
   * SEEDING DURING RENDER, GUARDED — the pattern `ReturnEditorScreen` and the
   * Delivery Note editor already use, and for the same two reasons: an effect
   * re-runs on every refetch and would overwrite whatever the user had just typed,
   * and `setState` inside an effect body causes a cascading render. The guard
   * seeds exactly once per opened record, so reopening the same row keeps the
   * edits already in progress.
   */
  if (open && loadedId !== targetId) {
    setState(
      party
        ? {
            name: party.name,
            code: party.code,
            gstin: party.gstin ?? '',
            stateCode: party.stateCode ?? '',
            billing: toText(party.billingAddressLines),
            shipping: toText(party.shippingAddressLines),
            shippingStateCode: party.shippingStateCode ?? '',
            phone: party.phone ?? '',
            email: party.email ?? '',
          }
        : BLANK,
    );
    setLoadedId(targetId);
    setSaveError(null);
  }

  const problems = useMemo(
    () =>
      collectProblems({
        name: partyNameField(state.name),
        gstin: gstinField(state.gstin),
        stateCode: stateCodeField(state.stateCode),
        email: emailField(state.email),
        phone: phoneField(state.phone),
        stateMatch: stateMatchesGstin(state.stateCode, state.gstin),
      }),
    [state],
  );

  const busy = create.isPending || update.isPending;
  /** True only when the FORM is satisfied; the service still has the final word. */
  const ready = Object.keys(problems).length === 0;

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setState((old) => ({ ...old, [key]: value }));
  }

  async function submit(force = false) {
    if (busy || submitting) return;
    setSubmitting(true);
    try {
      const payload = {
        kind,
        name: state.name.trim(),
        code: state.code.trim(),
        ...(state.gstin.trim() ? { gstin: state.gstin.trim().toUpperCase() } : {}),
        ...(state.stateCode.trim() ? { stateCode: state.stateCode.trim() } : {}),
        addressLines: lines(state.billing),
        ...(lines(state.shipping).length
          ? {
              shippingAddressLines: lines(state.shipping),
              ...(state.shippingStateCode.trim()
                ? { shippingStateCode: state.shippingStateCode.trim() }
                : {}),
            }
          : {}),
        ...(state.phone.trim() ? { phone: state.phone.trim() } : {}),
        ...(state.email.trim() ? { email: state.email.trim() } : {}),
      };

      let result;
      if (party) {
        result = await update.mutateAsync({ id: party.id, input: payload, force });
      } else {
        result = await create.mutateAsync({ ...payload, force });
      }

      if (!result.party && result.potentialDuplicate) {
        setDuplicateWarning(result.potentialDuplicate);
        return;
      }

      onClose();
    } catch (error) {
      setSaveError(error instanceof Error ? error.message : 'Could not save this customer.');
    } finally {
      setSubmitting(false);
    }
  }

  const title = party
    ? `Edit ${party.name}`
    : `New ${kind === 'SUPPLIER' ? 'supplier' : 'customer'}`;

  return (
    <Drawer open={open} onClose={onClose} title={title} locked={busy}>
      <Form
        aria-label={title}
        busy={busy}
        onSubmit={() => void submit()}
        footer={
          <>
            <Button type="submit" disabled={!ready || busy} className="flex-1">
              {party ? 'Save changes' : 'Create'}
            </Button>
            <Button type="button" variant="secondary" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
          </>
        }
      >
        {saveError && (
          <p
            role="alert"
            data-testid="party-save-error"
            className="rounded border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-200"
          >
            {saveError}
          </p>
        )}
        <Field label="Customer name" required error={problems.name}>
          {(f) => (
            <Input
              {...f}
              data-testid="party-name"
              value={state.name}
              invalid={Boolean(problems.name)}
              onChange={(e) => set('name', e.target.value)}
            />
          )}
        </Field>
        <Field label="Code" hint="Your own reference. Optional.">
          {(f) => (
            <Input
              {...f}
              data-testid="party-code"
              value={state.code}
              onChange={(e) => set('code', e.target.value)}
            />
          )}
        </Field>
        <Field
          label="GSTIN"
          hint="15 characters. Leave blank for an unregistered customer."
          error={problems.gstin ?? problems.stateMatch}
        >
          {(f) => (
            <Input
              {...f}
              data-testid="party-gstin"
              value={state.gstin}
              invalid={Boolean(problems.gstin ?? problems.stateMatch)}
              onChange={(e) => set('gstin', e.target.value)}
            />
          )}
        </Field>

        <Field label="State" error={problems.stateCode}>
          {(f) => (
            <select
              {...f}
              data-testid="party-state"
              value={state.stateCode}
              aria-invalid={Boolean(problems.stateCode) || undefined}
              className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
              onChange={(e) => set('stateCode', e.target.value)}
            >
              <option value="">Not set</option>
              {GST_STATES.map((s) => (
                <option key={s.code} value={s.code}>
                  {s.name}
                </option>
              ))}
            </select>
          )}
        </Field>

        <Field label="Phone" error={problems.phone}>
          {(f) => (
            <Input
              {...f}
              data-testid="party-phone"
              value={state.phone}
              invalid={Boolean(problems.phone)}
              onChange={(e) => set('phone', e.target.value)}
            />
          )}
        </Field>

        <Field label="Email" error={problems.email}>
          {(f) => (
            <Input
              {...f}
              data-testid="party-email"
              value={state.email}
              invalid={Boolean(problems.email)}
              onChange={(e) => set('email', e.target.value)}
            />
          )}
        </Field>

        <Field label="Billing address" hint="One line per line.">
          {(f) => (
            <Textarea
              {...f}
              data-testid="party-address"
              value={state.billing}
              onChange={(e) => set('billing', e.target.value)}
            />
          )}
        </Field>

        <Field
          label="Delivery address"
          hint="Leave blank when goods are delivered to the billing address."
        >
          {(f) => (
            <Textarea
              {...f}
              data-testid="party-shipping-address"
              value={state.shipping}
              onChange={(e) => set('shipping', e.target.value)}
            />
          )}
        </Field>

        {lines(state.shipping).length > 0 && (
          <Field
            label="Delivery state"
            hint="Used to decide place of supply when the two addresses differ."
          >
            {(f) => (
              <select
                {...f}
                data-testid="party-shipping-state"
                value={state.shippingStateCode}
                className="rounded border border-slate-700 bg-slate-900 px-2 py-1.5 text-sm text-slate-100"
                onChange={(e) => set('shippingStateCode', e.target.value)}
              >
                <option value="">Same as billing state</option>
                {GST_STATES.map((s) => (
                  <option key={s.code} value={s.code}>
                    {s.name}
                  </option>
                ))}
              </select>
            )}
          </Field>
        )}
      </Form>

      {duplicateWarning && (
        <ConfirmDialog
          open={true}
          title={`Possible duplicate ${kind === 'SUPPLIER' ? 'supplier' : 'customer'}`}
          message={
            `"${duplicateWarning.name}"` +
            (duplicateWarning.gstin ? ` (GSTIN: ${duplicateWarning.gstin})` : '') +
            ' already exists with the same name, phone, and email. ' +
            'This might be the same entity. No record has been saved yet — ' +
            'dismiss to edit the details, or confirm you want a separate entry.'
          }
          confirmLabel="Create anyway"
          busy={busy}
          onConfirm={() => {
            setDuplicateWarning(null);
            void submit(true);
          }}
          onCancel={() => setDuplicateWarning(null)}
        />
      )}
    </Drawer>
  );
}
